import React, { useEffect, useState } from 'react';
import Swal from '../librarianSwal';
import { useAuth } from '../../Constant/Auth';
import { useActiveLibraryId } from '../../Constant/useActiveLibrary';
import Loader from '../../Layout/Loader';
import useMinLoading from '../../Layout/useMinLoading';
import SearchableSelect from '../../shared/SearchableSelect';
import Pagination from '../components/Pagination';
import '../lib-page-header.css';

import {
  getStudentsByLibrary,
  addStudent,
  updateStudent,
  getErrorMessage
} from '../../api/api';

const PAGE_SIZE = 10;
const ADMISSION_NO_REGEX = /^[A-Za-z0-9/-]{3,}$/;
const MOBILE_REGEX = /^[6-9]\d{9}$/;

const STUDENT_STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' }
];

const parseCSV = (text) => {
  const lines = text.split(/\r?\n/).filter((line) => line.trim());
  if (lines.length < 2) return [];

  const headers = lines[0].split(',').map((h) => h.trim());

  return lines.slice(1).map((line) => {
    const values = line.split(',').map((v) => v.trim());
    const row = {};

    headers.forEach((header, index) => {
      row[header] = values[index] || '';
    });

    return row;
  });
};

const initialForm = {
  admission_no: '',
  gr_number: '',
  student_name: '',
  class_name: '',
  section: '',
  guardian_name: '',
  guardian_mobile: '',
  guardian_email: '',
  status: 'active'
};

const StudentList = () => {
  const { user } = useAuth();
  const libraryId = useActiveLibraryId();

  const [students, setStudents] = useState([]);
  const [rows, setRows] = useState([]);

  const [loading, setLoading] = useState(false);
  const showLoader = useMinLoading(loading);
  const [saving, setSaving] = useState(false);
  const [importLoading, setImportLoading] = useState(false);

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [editingStudentId, setEditingStudentId] = useState(null);

  const [formData, setFormData] = useState(initialForm);

  const fetchStudents = async () => {
    try {
      if (!libraryId) return;

      setLoading(true);
      const data = await getStudentsByLibrary(libraryId);
      setStudents(data || []);
    } catch (error) {
      Swal.fire('Error', getErrorMessage(error), 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStudents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [libraryId]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const openAddForm = () => {
    setEditingStudentId(null);
    setFormData(initialForm);
    setShowAddForm(true);
  };

  // Backends vary on the primary-key field name; resolve it once so Edit and
  // Deactivate never silently target an undefined id.
  const getStudentId = (student) =>
    student?.id ?? student?.student_id ?? student?._id ?? null;

  const openEditForm = (student) => {
    const studentId = getStudentId(student);

    if (!studentId) {
      Swal.fire('Error', 'This student record has no id and cannot be edited.', 'error');
      return;
    }

    setEditingStudentId(studentId);
    setFormData({
      admission_no: student.admission_no || '',
      // ?? (not ||) so an entered "0" pre-fills correctly instead of being
      // treated as empty.
      gr_number: student.gr_number ?? student.gr_no ?? '',
      student_name: student.student_name || student.full_name || '',
      class_name: student.class_name ?? student.class ?? '',
      section: student.section ?? student.section_name ?? '',
      guardian_name: student.guardian_name || student.parent_name || '',
      guardian_mobile: student.guardian_mobile || student.mobile || '',
      guardian_email: student.guardian_email || '',
      status: student.status || 'active'
    });
    setShowAddForm(true);
  };

  const closeAddForm = () => {
    setShowAddForm(false);
    setEditingStudentId(null);
    setFormData(initialForm);
  };

  const closeImportModal = () => {
    setShowImport(false);
    setRows([]);
  };

  const handleToggleStatus = async (student) => {
    const studentId = getStudentId(student);

    if (!studentId) {
      Swal.fire('Error', 'This student record has no id and cannot be updated.', 'error');
      return;
    }

    const newStatus = student.status === 'active' ? 'inactive' : 'active';

    const confirmed = await Swal.fire({
      title: `Mark "${student.student_name || student.full_name || 'this student'}" as ${newStatus}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: `Yes, mark ${newStatus}`,
      cancelButtonText: 'Cancel'
    });

    if (!confirmed.isConfirmed) return;

    try {
      // The update endpoint is a full update — it requires library_id,
      // admission_no and student_name, so send the whole record (not just the
      // new status) or the API rejects with a 400.
      await updateStudent(studentId, {
        library_id: libraryId,
        admission_no: student.admission_no,
        gr_number: student.gr_number ?? student.gr_no ?? null,
        student_name: student.student_name || student.full_name,
        class_name: student.class_name ?? student.class ?? null,
        section: student.section ?? student.section_name ?? null,
        guardian_name: student.guardian_name || student.parent_name,
        guardian_mobile: student.guardian_mobile || student.mobile,
        guardian_email: student.guardian_email || null,
        status: newStatus
      });
      Swal.fire('Success', `Student marked as ${newStatus}`, 'success');
      fetchStudents();
    } catch (error) {
      Swal.fire('Error', getErrorMessage(error), 'error');
    }
  };

  const handleAddStudent = async (e) => {
    e.preventDefault();

    if (!libraryId) {
      Swal.fire('Error', 'Library ID not found', 'error');
      return;
    }

    if (
      !formData.admission_no ||
      !formData.student_name ||
      !formData.class_name ||
      !formData.guardian_name ||
      !formData.guardian_mobile
    ) {
      Swal.fire(
        'Error',
        'Admission no, student name, class, guardian name and mobile are required',
        'error'
      );
      return;
    }

    if (!ADMISSION_NO_REGEX.test(formData.admission_no.trim())) {
      Swal.fire(
        'Error',
        'Admission no must be at least 3 characters and contain only letters, numbers, "-" or "/"',
        'error'
      );
      return;
    }

    const duplicateAdmission = students.some(
      (s) =>
        String(s.admission_no || '').toLowerCase() === formData.admission_no.trim().toLowerCase() &&
        getStudentId(s) !== editingStudentId
    );

    if (duplicateAdmission) {
      Swal.fire('Error', 'A student with this admission no already exists', 'error');
      return;
    }

    if (!MOBILE_REGEX.test(formData.guardian_mobile.trim())) {
      Swal.fire('Error', 'Enter a valid 10-digit guardian mobile number', 'error');
      return;
    }

    if (
      formData.guardian_email?.trim() &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.guardian_email.trim())
    ) {
      Swal.fire('Error', 'Enter a valid guardian email address', 'error');
      return;
    }

    try {
      setSaving(true);

      const payload = {
        library_id: libraryId,
        admission_no: formData.admission_no.trim(),
        gr_number: formData.gr_number?.trim() || null,
        student_name: formData.student_name.trim(),
        class_name: formData.class_name,
        section: formData.section || null,
        guardian_name: formData.guardian_name.trim(),
        guardian_mobile: formData.guardian_mobile.trim(),
        guardian_email: formData.guardian_email?.trim() || null,
        status: formData.status || 'active'
      };

      if (editingStudentId) {
        await updateStudent(editingStudentId, payload);
        Swal.fire('Success', 'Student updated successfully', 'success');
      } else {
        await addStudent(payload);
        Swal.fire('Success', 'Student added successfully', 'success');
      }

      closeAddForm();
      fetchStudents();
    } catch (error) {
      Swal.fire('Error', getErrorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();

    reader.onload = (event) => {
      const text = event.target.result;
      const parsedRows = parseCSV(text);

      if (parsedRows.length === 0) {
        Swal.fire('Error', 'CSV file is empty or invalid', 'error');
        return;
      }

      setRows(parsedRows);
    };

    reader.readAsText(file);
  };

  const handleImport = async () => {
    if (!libraryId) {
      Swal.fire('Error', 'Library ID not found', 'error');
      return;
    }

    if (rows.length === 0) {
      Swal.fire('Error', 'Please select CSV file first', 'error');
      return;
    }

    const confirm = await Swal.fire({
      title: 'Import Students?',
      text: `${rows.length} students will be imported.`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Import',
      cancelButtonText: 'Cancel'
    });

    if (!confirm.isConfirmed) return;

    try {
      setImportLoading(true);

      for (const row of rows) {
        const payload = {
          library_id: libraryId,
          admission_no: row.admission_no,
          gr_number: row.gr_number || row.gr_no || null,
          student_name: row.student_name,
          class_name: row.class_name,
          section: row.section || null,
          guardian_name: row.guardian_name,
          guardian_mobile: row.guardian_mobile,
          guardian_email: row.guardian_email || null,
          status: row.status || 'active'
        };

        await addStudent(payload);
      }

      Swal.fire('Success', 'Students imported successfully', 'success');

      setRows([]);
      setShowImport(false);
      fetchStudents();
    } catch (error) {
      Swal.fire('Error', getErrorMessage(error), 'error');
    } finally {
      setImportLoading(false);
    }
  };

  const downloadSample = () => {
    const csv = [
      'admission_no,gr_number,student_name,class_name,section,guardian_name,guardian_mobile,guardian_email,status',
      'ADM001,GR-1001,Rahul Sharma,5,A,Ramesh Sharma,9876543210,parent@example.com,active'
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = 'student_import_sample.csv';
    a.click();

    URL.revokeObjectURL(url);
  };

  const filteredStudents = students.filter((student) => {
    const keyword = search.toLowerCase();

    return (
      String(student.student_name || student.full_name || '').toLowerCase().includes(keyword) ||
      String(student.admission_no || '').toLowerCase().includes(keyword) ||
      String(student.gr_number ?? student.gr_no ?? '').toLowerCase().includes(keyword) ||
      String(student.class_name ?? student.class ?? '').toLowerCase().includes(keyword) ||
      String(student.section ?? student.section_name ?? '').toLowerCase().includes(keyword) ||
      String(student.guardian_name || student.parent_name || '').toLowerCase().includes(keyword) ||
      String(student.guardian_mobile || student.mobile || '').toLowerCase().includes(keyword)
    );
  });

  const totalPages = Math.max(1, Math.ceil(filteredStudents.length / PAGE_SIZE));
  const paginatedStudents = filteredStudents.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [search]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  return (
    <div className="container-fluid mt-3">
      <style>
        {`
          .sd-modal-overlay {
            position: fixed;
            inset: 0;
            background: rgba(15, 23, 42, 0.55);
            z-index: 9999;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 20px;
          }

          .sd-modal-box {
            width: 100%;
            max-width: 720px;
            max-height: 92vh;
            margin: auto;
            display: flex;
            flex-direction: column;
            background: #fff;
            border-radius: 14px;
            overflow: hidden;
            box-shadow: 0 30px 80px rgba(15, 23, 42, 0.35);
          }

          .sd-modal-head {
            padding: 14px 18px;
            background: linear-gradient(135deg, #4f46e5, #2563eb);
            color: #fff;
            display: flex;
            align-items: center;
            justify-content: space-between;
          }

          .sd-modal-head h5 {
            margin: 0;
            font-weight: 800;
            font-size: 17px;
          }

          .sd-modal-close {
            border: 0;
            background: rgba(255, 255, 255, 0.18);
            color: #fff;
            width: 32px;
            height: 32px;
            border-radius: 50%;
            font-size: 20px;
            line-height: 1;
          }

          /* <form> sits between .sd-modal-box (flex column) and the head/
             body/footer sections — without this it's a plain block box, so
             the sections never become real flex items and the body can
             never scroll (just gets clipped by the box's max-height). */
          .sd-modal-box form {
            display: contents;
          }

          .sd-modal-body {
            padding: 18px;
            flex: 1 1 auto;
            min-height: 0;
            overflow-y: auto;
          }

          /* 2-col CSS grid: spacing comes only from the grid gaps (no double
             margins), and inputs bottom-align so rows stay even on all sizes */
          .sd-modal-body .row {
            display: grid;
            grid-template-columns: 1fr 1fr;
            column-gap: 14px;
            row-gap: 14px;
            margin: 0;
          }
          .sd-modal-body .mb-3 { margin-bottom: 0 !important; }
          .sd-modal-body .form-label { display: block; margin-bottom: 5px; font-size: 12.5px; }
          .sd-modal-body .form-control,
          .sd-modal-body .form-select { width: 100%; box-sizing: border-box; min-height: 40px; }
          .sd-modal-body .row > [class*="col-md-"] {
            padding: 0;
            max-width: none;
            flex: none;
            display: flex;
            flex-direction: column;
          }
          .sd-modal-body .row > [class*="col-md-"] > .form-control,
          .sd-modal-body .row > [class*="col-md-"] > .form-select { margin-top: auto; }
          .sd-modal-body .row > .col-md-12 { grid-column: span 2; }

          .sd-modal-footer {
            padding: 14px 18px;
            border-top: 1px solid #eef2f7;
            display: flex;
            justify-content: flex-end;
            gap: 10px;
          }

          @media (max-width: 575px) {
            .sd-modal-box { max-width: 96vw; }
            .sd-modal-body .row { grid-template-columns: 1fr; }
            .sd-modal-body .row > [class*="col-md-"] { grid-column: span 1 !important; }
          }

          /* Filter/action row: Bootstrap collapses each .col-md-* to full
             width on mobile, but the buttons inside .col-md-8 (Add Student /
             Import CSV / Total badge) have no width rule of their own, so
             they just wrap ad-hoc based on label length — full-width Search
             and Reset next to inconsistently-sized buttons below. Give the
             action group an explicit, predictable stacked layout instead. */
          @media (max-width: 768px) {
            .sd-filter-row .col-md-8 {
              display: flex;
              flex-direction: column;
              align-items: stretch;
              gap: 8px;
              text-align: left;
            }
            .sd-filter-row .col-md-8 .btn {
              width: 100%;
              margin-right: 0 !important;
            }
            .sd-filter-row .col-md-8 .badge {
              align-self: flex-end;
            }
          }

          /* Student List table: 10 columns don't fit a phone width — drop the
             least-critical ones (GR Number/Section/Guardian/Contact) so # /
             Admission No / Student / Class / Status / Action stay visible
             without needing horizontal scroll to reach the edit action. */
          @media (max-width: 640px) {
            .sd-students-table .sd-col-gr,
            .sd-students-table .sd-col-section,
            .sd-students-table .sd-col-guardian,
            .sd-students-table .sd-col-contact {
              display: none;
            }
          }
        `}
      </style>

      <div className="lib-page-header">
        <div className="lib-page-header__left">
          <div className="lib-page-header__icon">
            <i className="fas fa-user-graduate" />
          </div>
          <div>
            <h3 className="lib-page-header__title">Students Data</h3>
            <div className="lib-page-header__subtitle">Add, import and view ERP/student records linked with parents</div>
          </div>
        </div>
      
      </div>

      <div className="card shadow-sm mb-3 table-full-card">
        <div className="card-body">
          <div className="row g-2 align-items-center">
            <div className="col-md-3">
              <input
                type="text"
                className="form-control"
                placeholder="Search student, admission no, class..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <div className="col-md-1">
              <button
                type="button"
                className="btn btn-outline-secondary w-100"
                onClick={() => setSearch('')}
              >
                Reset
              </button>
            </div>
              <div className="col-md-4">
          <button className="btn btn-outline-primary" onClick={downloadSample}>
            <i className="fas fa-download me-1" />
            Download Sample CSV
          </button>
        </div>

            <div className="col-md-4 text-md-end">
              <button
                type="button"
                className="btn btn-primary me-2"
                onClick={openAddForm}
              >
                <i className="fas fa-plus me-1" />
                Add Student
              </button>

              <button
                type="button"
                className="btn btn-outline-primary me-2"
                onClick={() => setShowImport((prev) => !prev)}
              >
                <i className="fas fa-file-import me-1" />
                Import CSV
              </button>

              <span className="badge bg-primary">
                Total: {students.length}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* {showAddForm && (
        <div className="sd-modal-overlay">
          <div className="sd-modal-box">
            <form onSubmit={handleAddStudent}>
              <div className="sd-modal-head">
                <h5><i className="fas fa-user-plus me-2" />{editingStudentId ? 'Edit Student' : 'Add Student'}</h5>
                <button type="button" className="sd-modal-close" onClick={closeAddForm} disabled={saving}>
                  ×
                </button>
              </div>

              <div className="sd-modal-body">
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Admission No *</label>
                    <input
                      type="text"
                      name="admission_no"
                      className="form-control"
                      value={formData.admission_no}
                      onChange={handleFormChange}
                      required
                    />
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label">Student Name *</label>
                    <input
                      type="text"
                      name="student_name"
                      className="form-control"
                      value={formData.student_name}
                      onChange={handleFormChange}
                      required
                    />
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label">Class *</label>
                    <input
                      type="text"
                      name="class_name"
                      className="form-control"
                      value={formData.class_name}
                      onChange={handleFormChange}
                      required
                    />
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label">Section</label>
                    <input
                      type="text"
                      name="section"
                      className="form-control"
                      value={formData.section}
                      onChange={handleFormChange}
                    />
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label">Status</label>
                    <select
                      name="status"
                      className="form-select"
                      value={formData.status}
                      onChange={handleFormChange}
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label">Guardian Name *</label>
                    <input
                      type="text"
                      name="guardian_name"
                      className="form-control"
                      value={formData.guardian_name}
                      onChange={handleFormChange}
                      required
                    />
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label">Guardian Mobile *</label>
                    <input
                      type="text"
                      name="guardian_mobile"
                      className="form-control"
                      value={formData.guardian_mobile}
                      onChange={handleFormChange}
                      required
                    />
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label">Guardian Email</label>
                    <input
                      type="email"
                      name="guardian_email"
                      className="form-control"
                      value={formData.guardian_email}
                      onChange={handleFormChange}
                    />
                  </div>
                </div>
              </div>

              <div className="sd-modal-footer">
                <button type="button" className="btn btn-light" onClick={closeAddForm} disabled={saving}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : editingStudentId ? 'Update' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )} */}


      {showAddForm && (
  <div className="sd-modal-overlay">
    <div className="sd-modal-box sd-modal-box--student">
      <form onSubmit={handleAddStudent}>
        <div className="sd-modal-head">
          <h5>
            <i className="fas fa-user-plus me-2" />
            {editingStudentId ? 'Edit Student' : 'Add Student'}
          </h5>

          <button
            type="button"
            className="sd-modal-close"
            onClick={closeAddForm}
            disabled={saving}
          >
            ×
          </button>
        </div>

        <div className="sd-modal-body">
          <div className="sd-student-grid">

            <div className="sd-field">
              <label className="form-label">Admission No *</label>
              <input
                type="text"
                name="admission_no"
                className="form-control"
                value={formData.admission_no}
                onChange={handleFormChange}
                required
              />
            </div>

            <div className="sd-field">
              <label className="form-label">GR Number</label>
              <input
                type="text"
                name="gr_number"
                className="form-control"
                value={formData.gr_number}
                onChange={handleFormChange}
              />
            </div>

            <div className="sd-field">
              <label className="form-label">Student Name *</label>
              <input
                type="text"
                name="student_name"
                className="form-control"
                value={formData.student_name}
                onChange={handleFormChange}
                required
              />
            </div>

            <div className="sd-field">
              <label className="form-label">Class *</label>
              <input
                type="text"
                name="class_name"
                className="form-control"
                value={formData.class_name}
                onChange={handleFormChange}
                required
              />
            </div>

            <div className="sd-field">
              <label className="form-label">Section</label>
              <input
                type="text"
                name="section"
                className="form-control"
                value={formData.section}
                onChange={handleFormChange}
              />
            </div>

            <div className="sd-field">
              <label className="form-label">Status</label>
              <SearchableSelect
                name="status"
                value={formData.status}
                onChange={handleFormChange}
                options={STUDENT_STATUS_OPTIONS}
                clearable={false}
              />
            </div>

            <div className="sd-field">
              <label className="form-label">Guardian Name *</label>
              <input
                type="text"
                name="guardian_name"
                className="form-control"
                value={formData.guardian_name}
                onChange={handleFormChange}
                required
              />
            </div>

            <div className="sd-field">
              <label className="form-label">Guardian Mobile *</label>
              <input
                type="text"
                name="guardian_mobile"
                className="form-control"
                value={formData.guardian_mobile}
                onChange={handleFormChange}
                required
              />
            </div>

            <div className="sd-field">
              <label className="form-label">Guardian Email</label>
              <input
                type="email"
                name="guardian_email"
                className="form-control"
                value={formData.guardian_email}
                onChange={handleFormChange}
              />
            </div>

          </div>
        </div>

        <div className="sd-modal-footer">
          <button
            type="button"
            className="btn btn-light"
            onClick={closeAddForm}
            disabled={saving}
          >
            Cancel
          </button>

          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving...' : editingStudentId ? 'Update' : 'Save'}
          </button>
        </div>
      </form>
    </div>
  </div>
)}

      {showImport && (
        <div className="card shadow-sm mb-3 table-full-card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <span className="fw-bold">
              <i className="fas fa-upload me-2" />
              Import Students CSV
            </span>
          </div>

          <div className="card-body">
            <label className="form-label">Upload CSV File</label>
            <div className="d-flex align-items-center gap-2">
              <input
                type="file"
                accept=".csv"
                className="form-control"
                onChange={handleFileChange}
              />
              <button
                className="btn btn-primary text-nowrap"
                onClick={handleImport}
                disabled={importLoading || rows.length === 0}
              >
                {importLoading ? 'Importing...' : 'Import Students'}
              </button>
            </div>

            <small className="text-muted">
              Required columns: admission_no, student_name, class_name, section,
              guardian_name, guardian_mobile. Optional: gr_number
            </small>

            {rows.length > 0 && (
              <div className="table-responsive mt-3">
                <table className="table table-bordered table-hover align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>#</th>
                      <th>Admission No</th>
                      <th>GR Number</th>
                      <th>Student</th>
                      <th>Class</th>
                      <th>Section</th>
                      <th>Guardian</th>
                      <th>Mobile</th>
                      <th>Email</th>
                      <th>Status</th>
                    </tr>
                  </thead>

                  <tbody>
                    {rows.map((row, index) => (
                      <tr key={index}>
                        <td>{index + 1}</td>
                        <td>{row.admission_no || '-'}</td>
                        <td>{row.gr_number || '-'}</td>
                        <td>{row.student_name || '-'}</td>
                        <td>{row.class_name || '-'}</td>
                        <td>{row.section || '-'}</td>
                        <td>{row.guardian_name || '-'}</td>
                        <td>{row.guardian_mobile || '-'}</td>
                        <td>{row.guardian_email || '-'}</td>
                        <td>{row.status || 'active'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="card shadow-sm table-full-card">
        <div className="card-header fw-bold">
          <i className="fas fa-user-graduate me-2" />
          Student List
        </div>

        <div className="card-body">
          {showLoader ? (
            <Loader />
          ) : (
            <div className="table-responsive">
              <table className="table table-bordered table-hover align-middle sd-students-table">
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>Admission No</th>
                    <th className="sd-col-gr">GR Number</th>
                    <th>Student</th>
                    <th>Class</th>
                    <th className="sd-col-section">Section</th>
                    <th className="sd-col-guardian">Guardian</th>
                    <th className="sd-col-contact">Contact</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {paginatedStudents.length > 0 ? (
                    paginatedStudents.map((student, index) => (
                      <tr key={getStudentId(student) || index}>
                        <td>{(page - 1) * PAGE_SIZE + index + 1}</td>
                        <td>{student.admission_no || '-'}</td>
                        <td className="sd-col-gr">{student.gr_number ?? student.gr_no ?? '-'}</td>
                        <td>
                          <strong>
                            {student.student_name || student.full_name || '-'}
                          </strong>
                        </td>
                        <td>{student.class_name ?? student.class ?? '-'}</td>
                        <td className="sd-col-section">{student.section ?? student.section_name ?? '-'}</td>
                        <td className="sd-col-guardian">{student.guardian_name || student.parent_name || '-'}</td>
                        <td className="sd-col-contact">{student.guardian_mobile || student.mobile || '-'}</td>
                        <td>
                          <span
                            className={`badge ${
                              student.status === 'active'
                                ? 'bg-success'
                                : 'bg-secondary'
                            }`}
                          >
                            {student.status || '-'}
                          </span>
                        </td>
                        <td>
                          <div className="d-flex gap-1">
                            <button
                              type="button"
                              className="btn btn-sm btn-primary"
                              onClick={() => openEditForm(student)}
                            >
                              Edit
                            </button>

                            <button
                              type="button"
                              className={`btn btn-sm ${
                                student.status === 'active'
                                  ? 'btn-warning'
                                  : 'btn-success'
                              }`}
                              onClick={() => handleToggleStatus(student)}
                            >
                              {student.status === 'active' ? 'Deactivate' : 'Activate'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="10" className="text-center text-muted py-4">
                        No students found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              <Pagination
                page={page}
                totalPages={totalPages}
                totalItems={filteredStudents.length}
                itemLabel="students"
                onPageChange={setPage}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StudentList;